import {
  Component,
  input,
  OnChanges,
  output,
  SimpleChanges,
  ChangeDetectionStrategy,
  viewChild,
} from '@angular/core';
import { Select2 } from 'ng-select2-component';
import { APP } from 'src/app/config/app';
import { IRouteAlteration } from 'src/app/models';
import { AlterationStatus } from 'src/app/models/enums';
import { LiteralService, UnsavedChangesService, UtilsService } from 'src/app/services';
import { TooltipPosition } from 'src/app/shared/tooltip/tooltip-position';
import { images } from 'src/images';

@Component({
  selector: 'app-route-alterations',
  templateUrl: './route-alterations.component.html',
  styleUrl: './route-alterations.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class RouteAlterationsComponent implements OnChanges {
  readonly alterations = input<IRouteAlteration[]>();
  readonly map = input<google.maps.Map>();
  readonly newAlteration = input<boolean>();
  readonly requestedAlterationId = input<number | null>(null);
  readonly onGoBack = output();
  readonly onChangeAlteration = output<IRouteAlteration>();
  readonly onNewAlteration = output();
  readonly onOpenAlterationDetail = output();
  readonly onDuplicateAlteration = output();

  public images = images;
  public TooltipPosition = TooltipPosition;

  private readonly alterationSelect = viewChild(Select2);

  public alterationsData: any = [];
  public selectedAlteration: any = null;

  constructor(
    public literalService: LiteralService,
    public utilsService: UtilsService,
    private unsavedChangesService: UnsavedChangesService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['alterations']) {
      this.alterationsData = changes['alterations'].currentValue.map(
        (alteration: IRouteAlteration) => ({
          id: alteration.id?.toString(),
          value: alteration,
          label: alteration.name!,
          img: alteration.active
            ? images.alterations.active
            : images.alterations.inactive,
          alt: alteration.active
            ? AlterationStatus.ACTIVE
            : AlterationStatus.INACTIVE,
        }),
      );
      const updatedList: IRouteAlteration[] =
        changes['alterations'].currentValue;
      const stillSelected = updatedList.find(
        (a: IRouteAlteration) => a.id === this.selectedAlteration?.id,
      );
      const requestedAlteration = updatedList.find(
        (alteration: IRouteAlteration) => alteration.id === this.requestedAlterationId(),
      );
      if (!this.selectedAlteration && requestedAlteration) {
        this.changeAlteration({ value: requestedAlteration });
      } else if (stillSelected) {
        this.selectedAlteration = stillSelected;
        this.changeAlteration({ value: stillSelected });
      } else if (updatedList.length > 0) {
        this.changeAlteration({ value: updatedList[0] });
      } else {
        this.changeAlteration({ value: null });
      }
    }
  }

  async selectAlteration(event: any) {
    if (event.value === this.selectedAlteration) return;
    if (!(await this.unsavedChangesService.confirmDiscard(APP.ROUTE_DESIGN_UNSAVED_CHANGES_KEY))) {
      this.alterationSelect()?.writeValue(this.selectedAlteration);
      return;
    }
    this.changeAlteration(event);
  }

  changeAlteration(event: any) {
    this.selectedAlteration = event.value;
    this.onChangeAlteration.emit(event.value);
  }

  onClickAlterationDetail() {
    this.onOpenAlterationDetail.emit();
  }

  duplicateAlteration() {
    this.onDuplicateAlteration.emit();
  }

  goBack() {
    this.onGoBack.emit();
  }
}
